<template>
  <div class="tasks-overview">
    <div class="tasks-overview__header">
      <h1 class="tasks-overview__title">{{ $t('opportunity-tasks.overview.title') }}</h1>
    </div>

    <div class="tasks-overview__progress-section">
      <div class="tasks-overview__progress-header">
        <span class="tasks-overview__progress-label">{{ $t('opportunity-tasks.overview.progress-label') }}</span>
        <span class="tasks-overview__progress-percent">{{ progressPercent }}%</span>
      </div>
      <div class="tasks-overview__progress-bar-wrap">
        <div class="tasks-overview__progress-bar-fill" :style="{ width: progressPercent + '%' }" />
      </div>
    </div>

    <div class="tasks-overview__details">
      <ul class="tasks-overview__status-list">
        <li v-for="taskStatus in taskStatuses" :key="taskStatus.id" class="tasks-overview__status-row">
          <span class="tasks-overview__status-row-label">{{ taskStatus.name }}</span>
          <span class="tasks-overview__status-row-count">{{
            isClosedTaskStatus(taskStatus)
              ? `${getTaskStatusCount(taskStatus)}/${tasks.length}`
              : getTaskStatusCount(taskStatus)
          }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TasksOverview',
  components: {},
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
    taskStatuses: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    progressPercent() {
      if (this.tasks.length === 0 || this.taskStatuses.length === 0) return 0;
      const closedStatus = this.taskStatuses.filter((taskStatus) => this.isClosedTaskStatus(taskStatus))[0];
      const closedCount = this.getTaskStatusCount(closedStatus);
      return ((closedCount / this.tasks.length) * 100).toFixed(0);
    },
  },
  methods: {
    isClosedTaskStatus(taskStatus) {
      return taskStatus.isReserved && taskStatus.statusType === 'closed';
    },
    getTaskStatusCount(taskStatus) {
      return this.tasks.reduce((count, task) => (task.taskStatus.id === taskStatus.id ? count + 1 : count), 0);
    },
  },
};
</script>

<style lang="scss">
.tasks-overview {
  background-color: $color-white;
  border-radius: $border-radius-root;
  padding: 32px;
  box-shadow: $shadow-default;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__title {
    margin: 0;
    font-size: $font-size-xbigger;
    line-height: 24px;
    font-weight: 900;
    color: $color-normal;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background-color: $color-green;
    border-radius: $border-radius-large;
    color: $color-white;
    font-size: $font-size-xsmall;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  &__status-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__progress-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 24px;
  }

  &__progress-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    width: 100%;
  }

  &__progress-label {
    flex-shrink: 0;
    font-size: $font-size-xxsmall;
    font-weight: 700;
    color: $color-normal-pale;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  &__progress-bar-wrap {
    width: 100%;
    height: 10px;
    background-color: $color-neutral-pale;
    border-radius: $border-radius-large;
    overflow: hidden;
  }

  &__progress-bar-fill {
    height: 100%;
    background-color: $color-accent;
    border-radius: $border-radius-large;
    transition: width 200ms ease;
  }

  &__progress-percent {
    flex-shrink: 0;
    font-size: $font-size-bigger;
    font-weight: 700;
    color: $color-accent;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-top: 1px solid $color-border;
    padding: 12px;
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__summary-text {
    font-size: $font-size-small;
    color: $color-accent;
  }

  &__summary-count {
    font-weight: 700;
    color: $color-accent;
  }

  &__summary-icon {
    width: 20px;
    height: 20px;
    color: $color-accent;
    flex-shrink: 0;
  }

  &__status-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: $font-size-small;
    color: $color-normal;
  }

  &__status-row-label {
    color: $color-normal-pale;
    font-weight: 600;
  }

  &__status-row-count {
    color: $color-normal;
    font-weight: 600;
  }
}
</style>
